import { store$ } from "@redux/store";
import { distinctUntilChanged, map, skip } from "rxjs";
import { selectNextPageUrl, selectPreviousPageUrl } from "../redux/selectors";

/**
 * createRelationElement: Creates a new Rel Element related to the paging.
 * @param {string} relationType It is the rel element type. It could be prev or next.
 * @returns An HTMLElement with the required rel information.
 */
function createRelationElement(relationType) {  
  const relationElement = document.createElement('link');
  relationElement.rel = relationType;
  document.head.appendChild(relationElement);

  return relationElement;
}

/**
 * getPageLink: Gets the DOM element that is going to be used as a page link.
 * @param {string} pageLinkType The page link type that is going to get.
 * @returns An HTMLElement related to the page link required.
 */
const getPageLink = (pageLinkType) => {
  const pageLink = document.querySelector(`[rel="${pageLinkType}"]`);
  return pageLink ?? createRelationElement(pageLinkType);
}

/**
 * removePageLink: Removes the page link element from the DOM.
 * @param {string} pageLinkType The page link type that is going to remove.
 */
const removePageLink = (pageLinkType) => {
  const previousPageLink = document.querySelector(`[rel="${pageLinkType}"]`);
  previousPageLink?.remove();
}

/**
 * 
 * @param {string} pageLinkUrl The url related to the page url element.
 * @param {string} pageLinkType Page link type that will be updated.
 */
const updatePageLink = (pageLinkUrl, pageLinkType) => {
  const pageLinkElement = getPageLink(pageLinkType);
  pageLinkElement.href = pageLinkUrl;
}

/**
 * handlePreviousPageLink: Handles the changes related to the Previous Page Link element.
 * @param {string} previousPageLinkUrl The url for the previous page link element.
 */
const handlePreviousPageLink = (previousPageLinkUrl) => {
  if (previousPageLinkUrl) {
    updatePageLink(previousPageLinkUrl, 'prev');
  } else {
    removePageLink('prev');
  }
}

/**
 * handleNextPageLink: Handles the changes related to the Next Page Link element.
 * @param {string} nextPageLinkUrl The url for the next page link element.
 */
const handleNextPageLink = (nextPageLinkUrl) => {
  if (nextPageLinkUrl) {
    updatePageLink(nextPageLinkUrl, 'next');
  } else {
    removePageLink('next');
  }
}

const nextPageUrlChange$ = store$.pipe(
  map(selectNextPageUrl),
  distinctUntilChanged(),
  skip(1)
);

nextPageUrlChange$.subscribe(handleNextPageLink);

const previousPageUrlChange$ = store$.pipe(
  map(selectPreviousPageUrl),
  distinctUntilChanged(),
  skip(1)
);

previousPageUrlChange$.subscribe(handlePreviousPageLink);